import React, { useState, useEffect, useRef } from "react";
import { Send, X, Shield, Search, MapPin, Battery, Globe, Cpu, Monitor, FileText } from "lucide-react";

export default function ForensicBot({ captures, isOpen, onClose }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const scrollRef = useRef(null);

    useEffect(() => {
        document.title = "Forensic AI Bot — Traxelon Automated Report Generator";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute("content", "AI-powered forensic intelligence assistant from Traxelon. Generate automated EXIF analysis reports, geolocation intelligence summaries, and device fingerprint briefs for cyber investigations.");
        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) canonical.setAttribute("href", "https://traxelon.com/forensic-bot");
        const ogUrl = document.querySelector('meta[property="og:url"]');
        if (ogUrl) ogUrl.setAttribute("content", "https://traxelon.com/forensic-bot");
    }, []);

    // Default suggestions for Quick Actions
    const suggestions = [
        { label: "Detailed Report", icon: <FileText className="w-3 h-3" />, cmd: "detailed report" },
        { label: "Geo Location", icon: <MapPin className="w-3 h-3" />, cmd: "geo location" },
        { label: "Network/IP", icon: <Globe className="w-3 h-3" />, cmd: "network status" },
        { label: "Battery/Power", icon: <Battery className="w-3 h-3" />, cmd: "battery status" },
        { label: "Hardware Specs", icon: <Cpu className="w-3 h-3" />, cmd: "device hardware" },
        { label: "Display Info", icon: <Monitor className="w-3 h-3" />, cmd: "display resolution" }
    ];

    useEffect(() => {
        if (!isOpen) {
            setMessages([]);
            setInput("");
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && messages.length === 0) {
            setMessages([{
                role: 'bot',
                text: `Forensic Intelligence System Online.\nIndexed Signals: ${captures.length}\n\nSelect a quick action or enter a search parameter.`
            }]);
        }
        if (scrollRef.current) scrollRef.current.scrollIntoView({ behavior: "smooth" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, messages]);

    const getShortLocation = async (lat, lon) => {
        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`);
            const data = await res.json();
            return data.address ? (data.address.amenity || data.address.building || data.address.road || "Point Alpha") : "Restricted";
        } catch (err) { return "Offline"; }
    };

    const processQuery = async (queryText) => {
        const query = queryText.toLowerCase();
        let response = "";

        // Expanded Keyword Mapping for Police/Forensic terminology
        const categories = [
            { 
                key: ["location", "geo", "gps", "coords", "where", "track", "map"], 
                label: "GEOGRAPHIC LOCATION", 
                emoji: "📡",
                type: "geo" 
            },
            { 
                key: ["battery", "power", "charge", "energy", "drain"], 
                label: "POWER SYSTEM", 
                emoji: "🔋",
                fields: { "Battery Level": "batteryLevel", "Charging Status": "isCharging", "Discharging Time": "dischargingTime" }
            },
            { 
                key: ["network", "ip", "isp", "connection", "wifi", "signal", "provider", "internet"], 
                label: "NETWORK INTELLIGENCE", 
                emoji: "🌐",
                fields: { "IP Address": "ip", "ISP": "isp", "Organisation": "organisation", "Connection": "connectionType", "Downlink": "downlink" }
            },
            { 
                key: ["device", "hardware", "ram", "cpu", "gpu", "os", "specs", "system", "processor"], 
                label: "DEVICE & HARDWARE", 
                emoji: "📱",
                fields: { "OS": "os", "Platform": "platform", "CPU Cores": "cpuCores", "RAM": "ram", "GPU": "gpu" }
            },
            {
                key: ["screen", "display", "resolution", "monitor", "view"],
                label: "SCREEN & DISPLAY",
                emoji: "🖥️",
                fields: { "Resolution": "resolution", "Available Space": "available", "Orientation": "orientationType" }
            }
        ];

        const isDetailedRequest = 
            query.includes("detailed") || 
            query.includes("report") || 
            query.includes("reort") || 
            query.includes("brief") || 
            query.includes("summary") || 
            query.includes("full") || 
            query.includes("all");

        if (isDetailedRequest) {
            response = `📋 COMPREHENSIVE FORENSIC BRIEFING\n`;
            response += `Total Signals Processed: ${captures.length}\n`;
            response += `===================================\n`;
            for (const [index, c] of captures.entries()) {
                response += `\n🎯 TARGET SOURCE #0${index + 1}\n`;
                response += `-----------------------------------\n`;
                response += `Timestamp: ${c.capturedAt ? new Date(c.capturedAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "N/A"}\n`;
                response += `IP Address: ${c.ip || 'Restricted'}\n`;
                response += `ISP/Network: ${c.isp || 'Restricted'}\n`;
                response += `Location: ${c.city || 'Unknown'}, ${c.region || 'Unknown'}, ${c.country || 'India'}\n`;
                if (c.gpsLat && c.gpsLon) {
                    const landmark = await getShortLocation(c.gpsLat, c.gpsLon);
                    response += `Coordinates: ${c.gpsLat.toFixed(6)}, ${c.gpsLon.toFixed(6)}\n`;
                    response += `Est. Address: ${landmark}\n`;
                }
                response += `Operating System: ${c.os || 'Restricted'}\n`;
                response += `Browser: ${c.browser || 'Restricted'}\n`;
                response += `Screen Res: ${c.screenWidth && c.screenHeight ? `${c.screenWidth}x${c.screenHeight}` : 'Restricted'}\n`;
                response += `Battery Level: ${c.batteryLevel !== undefined && c.batteryLevel !== null ? `${c.batteryLevel}%` : 'Restricted'}\n`;
                response += `Power Status: ${c.isCharging !== undefined && c.isCharging !== null ? (c.isCharging === true || c.isCharging === "true" ? "Charging" : "Discharging") : 'Restricted'}\n`;
                response += `VPN/Proxy Detected: ${c.isVpn || c.isProxy ? "Yes" : "No"}\n`;
            }
            response += `\n===================================\n`;
            response += `END OF BRIEFING`;
        } else {
            const active = categories.find(cat => cat.key.some(k => query.includes(k)));

            if (active) {
                response = `${active.emoji} ${active.label} REPORT\n`;
                for (const [index, c] of captures.entries()) {
                    response += `\n🔹 SIGNAL SOURCE #0${index + 1}\n`;
                    if (active.type === "geo") {
                        const landmark = (c.gpsLat && c.gpsLon) ? await getShortLocation(c.gpsLat, c.gpsLon) : "Restricted";
                        response += `Country: ${c.country || 'India'}\n`;
                        response += `State: ${c.region || 'Karnataka'}\n`;
                        response += `City: ${c.city || 'Mangaluru'}\n`;
                        response += `Location: ${landmark}\n`;
                        response += `Coordinates:\n`;
                        response += `Latitude: ${c.gpsLat ? c.gpsLat.toFixed(6) : '0.000000'}\n`;
                        response += `Longitude: ${c.gpsLon ? c.gpsLon.toFixed(6) : '0.000000'}\n`;
                    } else {
                        for (const [label, key] of Object.entries(active.fields)) {
                            let val = c[key];
                            let display = "Restricted";
                            if (val !== undefined && val !== null && val !== "") {
                                display = val;
                                if (key === "batteryLevel") display = `${val}%`;
                                if (key === "ram") display = `${val} GB`;
                                if (key === "dischargingTime") display = `${val}s`;
                                if (key === "downlink") display = `${val} Mbps`;
                                if (key === "isCharging") display = (val === true || val === "true") ? "Charging" : "Discharging";
                            }
                            response += `${label}: ${display}\n`;
                        }
                    }
                }
            } else {
                response = `System: No match for "${queryText}".\nTry keywords like: Detailed Report, Geo Location, IP Track, or Hardware Specs.`;
            }
        }

        setMessages(prev => [...prev, { role: 'bot', text: response }]);
    };

    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim()) return;
        const text = input;
        setMessages(prev => [...prev, { role: 'user', text }]);
        setInput("");
        await processQuery(text);
    };

    const handleSuggestion = async (cmd) => {
        setMessages(prev => [...prev, { role: 'user', text: cmd }]);
        await processQuery(cmd);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[1000] flex items-center justify-center p-4">
            <div className="bg-slate-900 border border-cyan-500/30 rounded-2xl w-full max-w-lg h-[600px] flex flex-col shadow-2xl overflow-hidden shadow-cyan-500/10">
                {/* Header */}
                <div className="bg-cyan-950/20 border-b border-slate-800 p-4 flex justify-between items-center">
                    <div className="flex items-center gap-2 text-cyan-400 font-mono text-sm tracking-tighter">
                        <Shield className="w-4 h-4 animate-pulse" /> FORENSIC INTEL BOT
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors"><X /></button>
                </div>

                {/* Chat Area */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-950/50 scrollbar-hide">
                    {messages.map((m, i) => (
                        <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[90%] p-3 rounded-xl font-mono text-[11px] border ${
                                m.role === 'user' ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-100' : 'bg-slate-900 border-slate-800 text-slate-300'
                            } whitespace-pre-wrap`}>
                                {m.text}
                            </div>
                        </div>
                    ))}
                    <div ref={scrollRef} />
                </div>

                {/* Suggestions UI */}
                <div className="px-4 py-2 flex flex-wrap gap-2 border-t border-slate-800 bg-slate-950/80">
                    {suggestions.map((s, i) => (
                        <button 
                            key={i} 
                            onClick={() => handleSuggestion(s.cmd)}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-slate-900 border border-slate-700 text-[10px] text-slate-400 hover:border-cyan-500 hover:text-cyan-400 transition-all font-mono uppercase tracking-wider"
                        >
                            {s.icon} {s.label}
                        </button>
                    ))}
                </div>

                {/* Input Area */}
                <form onSubmit={handleSend} className="p-4 bg-slate-950 flex gap-2">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                        <input 
                            className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-9 pr-3 py-2 text-xs font-mono text-cyan-400 focus:outline-none focus:border-cyan-500 transition-all"
                            placeholder="Input target parameter..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                        />
                    </div>
                    <button type="submit" className="p-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-500 shadow-lg shadow-cyan-900/20">
                        <Send className="w-4 h-4" />
                    </button>
                </form>
            </div>
        </div>
    );
}